(() => {
  'use strict';
  const config = document.getElementById('oa-search-analytics-config');
  if (!config || window.__oaSearchAnalyticsV1 || config.dataset.designMode === 'true') return;
  window.__oaSearchAnalyticsV1 = true;
  const pendingKey = 'oa_search_intent_v1';
  const debugKey = 'oa_search_debug_v1';
  const storage = {
    get(key) { try { return sessionStorage.getItem(key); } catch (_) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch (_) {} },
    remove(key) { try { sessionStorage.removeItem(key); } catch (_) {} }
  };
  const debugOption = new URL(location.href).searchParams.get('oa_debug');
  if (debugOption === '1') storage.set(debugKey, '1');
  if (debugOption === '0') storage.remove(debugKey);
  const debug = debugOption === '1' || (debugOption !== '0' && storage.get(debugKey) === '1');
  if (!debug && window.Shopify?.theme?.role !== 'main') return;
  const clean = value => String(value || '').normalize('NFKC').replace(/\s+/g, ' ').trim();
  const short = value => clean(value).slice(0, 100);
  const term = value => {
    const text = clean(value).toLowerCase();
    if (!text || text === '*') return '';
    if (text.length > 80 || /@|https?:|www\.|\b(?:title|tag|body|variants\.[a-z]+):/i.test(text)) return '';
    if (/(?:\d[\s()+.\-]*){7,}/.test(text) || /\b\d+\s+.+\b(street|road|avenue|lane|drive|apartment|apt)\b/i.test(text)) return '';
    return text;
  };
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  let intent = null;
  let ready = false;
  let base = null;
  let lastClick = { key: '', at: 0 };
  const root = () => document.querySelector('[data-oa-instant-section]');
  const snapshot = () => {
    const block = root()?.querySelector('oa-search-filters');
    const url = new URL(location.href);
    const context = config.dataset.pageType === 'search' ? 'search' : 'collection';
    const rawTerm = block?.dataset.query ?? (context === 'search' ? config.dataset.term : '');
    const data = {
      oa_context: context,
      oa_result_type: context === 'search' ? (url.searchParams.get('type') || 'all') : 'product',
      oa_term_status: !clean(rawTerm) || clean(rawTerm) === '*' ? 'none' : (term(rawTerm) ? 'recorded' : 'omitted'),
      oa_category: '(any)', oa_product_type: '(any)', oa_occasion: '(any)', oa_recipient: '(any)',
      oa_budget: '(any)', oa_availability: '(any)', oa_native_filters: '(any)'
    };
    if (term(rawTerm)) data.oa_term = term(rawTerm);
    block?.querySelectorAll('select[data-oa-tag]').forEach(select => {
      const key = 'oa_' + select.dataset.oaTag;
      if (Object.prototype.hasOwnProperty.call(data, key) && select.value) data[key] = short(select.selectedOptions[0]?.textContent || select.value);
    });
    const min = url.searchParams.get('filter.v.price.gte');
    const max = url.searchParams.get('filter.v.price.lte');
    if (min || max) data.oa_budget = `${min || '0'}–${max || 'any'} ${window.Shopify?.currency?.active || ''}`.trim().slice(0, 100);
    const availability = [...new Set(url.searchParams.getAll('filter.v.availability'))].sort().join(',');
    if (availability) data.oa_availability = availability === '1' ? 'in_stock' : availability === '0' ? 'out_of_stock' : 'all_stock_statuses';
    const native = [...(block?.querySelectorAll('input[type="checkbox"][name^="filter."]:checked') || [])]
      .filter(input => input.name !== 'filter.v.availability')
      .map(input => `${clean(input.closest('fieldset')?.querySelector('legend')?.textContent || input.name)}: ${clean(input.closest('label')?.querySelector('span')?.textContent || input.value).replace(/\s*\(\d+\)$/, '')}`).sort().join(' | ');
    data._native = native;
    if (native) data.oa_native_filters = short(native);
    const count = Number(block?.dataset.count ?? (context === 'search' ? config.dataset.count : NaN));
    if (Number.isFinite(count) && count >= 0) data.oa_result_count = count;
    return data;
  };
  const signature = data => JSON.stringify(Object.fromEntries(Object.entries(data).filter(([key]) => key !== 'oa_result_count')));
  const hasFilters = data => ['oa_category', 'oa_product_type', 'oa_occasion', 'oa_recipient', 'oa_budget', 'oa_availability', 'oa_native_filters'].some(key => data[key] !== '(any)');
  const emit = (name, data) => {
    if (!ready || !allowed()) return;
    try {
      const promise = window.Shopify?.analytics?.publish?.('oakenark:' + name, {
        ...Object.fromEntries(Object.entries(data).filter(([key]) => !key.startsWith('_'))), oa_environment: debug ? 'test' : 'production'
      });
      promise?.catch?.(() => {});
    } catch (_) {}
  };
  const mark = (kind, source, action, destination) => {
    if (!allowed()) return;
    intent = { kind, source, action, at: Date.now(), path: destination?.pathname || location.pathname };
    if (destination) storage.set(pendingKey, JSON.stringify(intent));
  };
  const report = (action, data) => {
    if (action.kind === 'search') {
      if (!data.oa_term && data.oa_term_status !== 'omitted') return;
      emit('search', { ...data, oa_source: action.source });
    } else {
      emit('filter', { ...data, oa_source: action.source, oa_action: action.action || 'change' });
    }
    if (data.oa_result_count === 0) emit('no_results', {
      ...data, oa_source: action.source,
      oa_zero_reason: action.kind === 'filter' || hasFilters(data) ? 'filters' : 'keyword'
    });
  };
  const safeURL = href => { try { return new URL(href, location.origin); } catch (_) { return null; } };
  const isSearchURL = url => /\/search\/?$/.test(url?.pathname || '');
  const nativeSearch = (url, source) => {
    if (!url || url.origin !== location.origin || !isSearchURL(url)) return;
    const query = url.searchParams.get('q') || '';
    const categoryShortcut = /^\s*\(?\s*tag\s*:|\sAND\s+\(\s*tag\s*:/i.test(query);
    mark(categoryShortcut ? 'filter' : 'search', source, categoryShortcut ? 'shortcut' : '', url);
  };
  const filterControl = event => {
    const node = event.target;
    if (node instanceof Element && node.matches('#SortBy,select[name="sort_by"]')) { intent = null; storage.remove(pendingKey); return; }
    if (node instanceof Element && node.closest('oa-search-filters') && node.matches('select[data-oa-tag],input[data-price-min],input[data-price-max],input[type="checkbox"][name^="filter."]')) mark('filter', 'filters', 'change');
  };
  window.addEventListener('change', filterControl, true);
  window.addEventListener('input', event => {
    if (event.target instanceof Element && event.target.matches('[data-price-min],[data-price-max]')) filterControl(event);
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.checkValidity()) return;
    if (form.closest('oa-search-filters')) { mark('filter', 'filters', 'change', safeURL(form.action)); return; }
    if (!form.closest('oa-search')) return;
    const url = safeURL(form.action);
    if (!url) return;
    url.search = new URLSearchParams(new FormData(form));
    nativeSearch(url, form.closest('oa-search').dataset.context === 'search-page' ? 'search_page' : 'header');
  }, true);
  window.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || event.button > 0) return;
    const link = target.closest('a[href]');
    const url = link && safeURL(link.href);
    const modified = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link?.target === '_blank';
    const predictive = target.closest('.oa-search__product');
    const card = target.closest('.grid-product[data-product-handle]');
    const list = card?.closest('.grid__item--content');
    if (url?.origin === location.origin && /\/products\/[^/]+/.test(url.pathname) && (predictive || (card && list && root()?.contains(list)))) {
      const handle = decodeURIComponent(url.pathname.split('/products/')[1].split('/')[0]);
      const clickKey = location.pathname + location.search + '|' + handle;
      if (lastClick.key === clickKey && Date.now() - lastClick.at < 500) return;
      lastClick = { key: clickKey, at: Date.now() };
      let data;
      if (predictive) {
        const box = predictive.closest('oa-search');
        const rawTerm = box?.querySelector('[name="q"]')?.value || '';
        data = { oa_context: 'predictive', oa_source: 'predictive', oa_term_status: term(rawTerm) ? 'recorded' : 'omitted' };
        if (term(rawTerm)) data.oa_term = term(rawTerm);
        data.oa_position = [...box.querySelectorAll('.oa-search__product')].indexOf(predictive) + 1;
      } else {
        if (root()?.getAttribute('aria-busy') === 'true') return;
        data = { ...snapshot(), oa_source: config.dataset.pageType === 'search' ? 'search_results' : 'collection' };
        data.oa_position = [...list.querySelectorAll('.grid-product[data-product-handle]')].indexOf(card) + 1;
      }
      if ((predictive || card).dataset.productId) data.oa_product_id = short((predictive || card).dataset.productId);
      data.oa_product_handle = short(handle);
      data.oa_product_name = short((predictive || card).querySelector('.oa-search__name,.grid-product__title')?.textContent || handle);
      emit('product_click', data);
      return;
    }
    if (modified) return;
    if (target.closest('oa-search-filters [data-budget-min]')) { mark('filter', 'filters', 'budget'); return; }
    if (link?.matches('[data-remove-query],[data-collection-path]') || link?.closest('.oa-refine__active') || link?.matches('.oa-refine__actions > a,.oa-search-page__empty .btn')) {
      mark('filter', 'filters', link?.matches('[data-remove-query],[data-collection-path]') ? 'remove' : 'clear', url);
      return;
    }
    if (link?.closest('.oa-search__shortcuts,.oa-search__all,.oa-search__help') && isSearchURL(url)) { nativeSearch(url, 'shortcut'); return; }
    if (link?.closest('.pagination,.oa-search-page__tabs')) { intent = null; storage.remove(pendingKey); }
  }, true);
  window.addEventListener('popstate', () => { intent = null; storage.remove(pendingKey); }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted) { intent = null; base = snapshot(); storage.remove(pendingKey); }
  });
  document.addEventListener('collection:reloaded', () => {
    const next = snapshot();
    const action = intent;
    intent = null;
    storage.remove(pendingKey);
    if (action?.kind === 'filter' && Date.now() - action.at < 120000 && base && signature(base) !== signature(next)) report(action, next);
    base = next;
  });
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { intent = null; storage.remove(pendingKey); }
  });
  const start = () => {
    if (ready) return;
    ready = true;
    base = snapshot();
    let pending;
    try { pending = JSON.parse(storage.get(pendingKey) || 'null'); } catch (_) {}
    storage.remove(pendingKey);
    const navigation = performance.getEntriesByType?.('navigation')[0]?.type;
    if (!allowed() || ['reload', 'back_forward'].includes(navigation)) return;
    if (pending && Date.now() - pending.at < 120000 && pending.path === location.pathname && ['search', 'filter'].includes(pending.kind)) {
      report(pending, base);
    } else if (config.dataset.pageType === 'search' && config.dataset.performed === 'true' && base.oa_term && !new URL(location.href).searchParams.has('page')) {
      report({ kind: 'search', source: 'direct_link' }, base);
    }
  };
  if (window.Shopify?.customerPrivacy) start();
  else if (window.Shopify?.loadFeatures) {
    try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], start); } catch (_) { start(); }
  } else start();
})();
